import type { KeyboardEvent } from 'react'
import { Snowflake, Thermometer, Zap } from '../icons'
import { shortId } from '../lib/format'
import type { Sensor, SensorIcon } from '../types/device'

interface Props {
  sensor: Sensor
  selected: boolean
  onSelect: (id: string) => void
}

function SensorBadgeIcon({ name }: { name: SensorIcon }) {
  switch (name) {
    case 'Snowflake': return <Snowflake />
    case 'Zap': return <Zap />
    case 'Thermometer': return <Thermometer />
    default: return <Thermometer />
  }
}

function primaryReading(sensor: Sensor): string {
  const r = sensor.readings
  if (sensor.kind === 'elettrico' && r.power != null) {
    return r.power >= 1000 ? `${(r.power / 1000).toFixed(2)} kW` : `${r.power.toFixed(0)} W`
  }
  if (r.temperature != null) {
    return `${r.temperature.value.toFixed(1)}°C`
  }
  return '—'
}

function ageLabel(ageMinutes: number | null): string {
  if (ageMinutes == null) return 'mai'
  if (ageMinutes < 1) return 'pochi sec fa'
  if (ageMinutes < 60) return `${ageMinutes}m fa`
  const hours = Math.floor(ageMinutes / 60)
  if (hours < 24) return `${hours}h fa`
  return `${Math.floor(hours / 24)}g fa`
}

export function SensorTile({ sensor, selected, onSelect }: Props) {
  const state = sensor.online ? 'on' : 'offline'
  const handleKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onSelect(sensor.id)
    }
  }

  return (
    <div
      className="ctile"
      data-state={state}
      data-on={sensor.online}
      data-selected={selected}
      onClick={() => onSelect(sensor.id)}
      role="button"
      tabIndex={0}
      onKeyDown={handleKey}
    >
      <div className="ctile__icon">
        <SensorBadgeIcon name={sensor.icon} />
      </div>
      <div className="ctile__text">
        <div className="ctile__name">{sensor.name}</div>
        <div className="ctile__meta">
          {sensor.online ? (
            <>
              <span className="ctile__dot on"></span>
              <span className="ctile__power">{primaryReading(sensor)}</span>
            </>
          ) : (
            <>
              <span className="ctile__dot offline"></span>
              <span className="ctile__muted">Offline · {ageLabel(sensor.ageMinutes)}</span>
            </>
          )}
          <span className="ctile__id">{shortId(sensor.id)}</span>
        </div>
      </div>
      <div className="ctile__meter" aria-label="Solo misurazione">
        <SensorBadgeIcon name={sensor.icon} />
      </div>
    </div>
  )
}
